import {
  Box,
  FormControl,
  HStack,
  VStack,
  Icon,
  Text,
  Button,
  Select,
  IconButton,
  FormLabel,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Spinner,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { FiChevronDown } from "react-icons/fi";
import React, { useState } from "react";
import { FiPlus, FiTrash2, FiFilter, FiEdit2, FiCopy, FiX } from "react-icons/fi";
import { DataScopeProto } from "build-ts/analyst";
import { FilterProto, Header } from "build-ts/metadata";
import { DateFilterCategory } from "utils/enum";
import { DateFilter } from "build-ts/relative_date";
import { ColumnType, FilterOperator } from "build-ts/enums";
import GlobalEntity from "containers/batch/GlobalEntity";
import { getFilterDisplayString } from "utils/nameUtils";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterCategory } from "utils/dateUtils";
import { KGObservationProto } from "build-ts/analysis_plan";
import { ObservationColumnProto } from "build-ts/metadata";
import { getKGObservationData } from "services/AnalysisTreeServices";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { getTooltip } from "utils/getMessages";

interface PresetFiltersProps {
  dataScope: DataScopeProto | undefined;
  bulkPresetFiltersMap: Map<string, FilterProto[]>;
  setBulkPresetFiltersMap: React.Dispatch<React.SetStateAction<Map<string, FilterProto[]>>>;
  isEditingScenario: boolean;
  isTabActive: number;
}

const PresetFilters: React.FC<PresetFiltersProps> = ({
  dataScope,
  bulkPresetFiltersMap,
  setBulkPresetFiltersMap,
  isEditingScenario,
  isTabActive
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null); // for editing existing filter
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Form state for creating/editing preset filter
  const [selectedAttributes, setSelectedAttributes] = useState<Header[]>([]);
  const [attributeFilters, setAttributeFilters] = useState<FilterProto[]>([]);
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);

  // State for editing a specific attribute filter
  const [editingAttributeFilter, setEditingAttributeFilter] = useState<FilterProto | null>(null);
  const [editingAttributeIndex, setEditingAttributeIndex] = useState<number>(-1);
  const [valuesList, setValuesList] = useState<string[]>([]);
  const [isLoadingValues, setIsLoadingValues] = useState<boolean>(false);
  const [loadingFilterIndex, setLoadingFilterIndex] = useState<number>(-1); // Track which filter is loading
  const [showValuesSection, setShowValuesSection] = useState<boolean>(false);
  const [selectedFilterOperator, setSelectedFilterOperator] = useState<FilterOperator>(FilterOperator.EQ);
  const [selectedFilterValues, setSelectedFilterValues] = useState<string[]>([]);

  // Get attribute columns from dataScope (exclude hidden filters)
  const attributeColumns = dataScope?.column_scopes?.filter(
    cs => cs?.kg_column?.header?.column_type === ColumnType.ATTRIBUTE && !cs.is_hidden
  ) || [];

  // Get temporal columns
  const temporalColumns = dataScope?.column_scopes?.filter(
    cs => cs?.kg_column?.header?.column_type === ColumnType.TEMPORAL
  ) || [];

  // Get saved filters count (only count non-empty filter sets so we don't show Edit/Delete for empty rows)
  const savedFiltersCount = Array.from(bulkPresetFiltersMap.values()).filter((f) => f?.length > 0).length;

  // Toggle attribute selection
  const toggleAttributeSelection = (header: Header) => {
    if (!header.id) return;

    const isCurrentlySelected = selectedAttributes.some(attr => attr.id === header.id);

    if (isCurrentlySelected) {
      // Deselect: Remove from selectedAttributes
      setSelectedAttributes(prev => prev.filter(attr => attr.id !== header.id));

      // Also remove from attributeFilters
      setAttributeFilters(prev => prev.filter(filter => filter.kg_column_header?.id !== header.id));
    } else {
      // Select: Add to selectedAttributes
      setSelectedAttributes(prev => [...prev, header]);

      // Add to attributeFilters without operator/values initially
      const newFilter = FilterProto.create({
        filter_id: header.id,
        kg_column_header: header,
      });
      setAttributeFilters(prev => [...prev, newFilter]);
    }
  };

  // Fetch values for attribute filters for the given column
  const fetchValuesForAttributeFilters = async (column: Header) => {
    if (!column) return;

    const filters: FilterProto[] = [];
    for (const filter of dataScope?.column_scopes || []) {
      if (filter?.kg_column?.header?.id === column.id) {
        continue;
      }
      if (filter.operator && filter.values && filter.values.length > 0) {
        filters.push(FilterProto.create({
          filter_id: filter?.kg_column?.header?.id,
          kg_column_header: filter?.kg_column?.header,
          operator: filter.operator,
          values: filter.values,
          calculated_date_ranges: [],
        }));
      }
    }

    // Create filters with is_hidden=false for the request
    const filtersForRequest = filters.map(f => ({
      ...f,
      is_hidden: false
    }));

    const request = KGObservationProto.create({
      metrics: dataScope?.metrics || [],
      columns: [ObservationColumnProto.create({
        column_id: column.id,
        kg_column_header: column,
      })],
      filters: filtersForRequest,
      calculate_non_measure_only: true,
    });
    const values = await getKGObservationData([request]);
    const valuesList = JSON.parse(values?.[0]?.kg_observation_data?.non_measure_values[0]?.data_value_json_array || "[]");
    return valuesList;
  };

  // Fetch values for editing an attribute filter
  const fetchValuesForEditingAttributeFilter = async (filter: FilterProto, filterIndex: number) => {
    if (!filter.kg_column_header) return;

    setIsLoadingValues(true);
    setLoadingFilterIndex(filterIndex); // Set which filter is loading
    setEditingAttributeFilter(filter);
    setEditingAttributeIndex(filterIndex);

    try {
      // Check if column has values in dataScope first
      const columnScope = dataScope?.column_scopes?.find(
        cs => cs?.kg_column?.header?.id === filter?.kg_column_header?.id
      );

      if (columnScope?.values && columnScope.values.length > 0) {
        setValuesList(columnScope.values);
      } else {
        // Fetch values using the validFilters
        const values = await fetchValuesForAttributeFilters(filter.kg_column_header);
        setValuesList(values || []);
      }

      // Set the editing state
      setSelectedFilterOperator(filter.operator || FilterOperator.EQ);
      setSelectedFilterValues(filter.values || []);
      setShowValuesSection(true);
    } catch (error) {
      console.error("Error fetching attribute values for editing:", error);
      setValuesList([]);
    } finally {
      setIsLoadingValues(false);
      setLoadingFilterIndex(-1); // Reset loading filter index
    }
  };

  // Save edited attribute filter
  const handleSaveEditedAttributeFilter = () => {
    if (!editingAttributeFilter || editingAttributeIndex === -1 || selectedFilterValues.length === 0) {
      return;
    }

    // Update the filter at the specific index
    setAttributeFilters((prev) => {
      const updated = [...prev];
      updated[editingAttributeIndex] = FilterProto.create({
        filter_id: editingAttributeFilter.filter_id,
        kg_column_header: editingAttributeFilter.kg_column_header,
        operator: selectedFilterOperator,
        values: selectedFilterValues,
      });
      return updated;
    });

    // Reset editing state
    handleCancelEditAttribute();
  };

  // Cancel editing attribute
  const handleCancelEditAttribute = () => {
    setEditingAttributeFilter(null);
    setEditingAttributeIndex(-1);
    setSelectedFilterValues([]);
    setSelectedFilterOperator(FilterOperator.EQ);
    setShowValuesSection(false);
    setValuesList([]);
    setLoadingFilterIndex(-1); // Reset loading state
  };

  // Remove attribute filter
  const handleRemoveAttributeFilter = (filterId: string) => {
    setAttributeFilters(prev => prev.filter(filter => filter.filter_id !== filterId));
    setSelectedAttributes(prev => prev.filter(attr => attr.id !== filterId));
  };

  const handleAddNewFilter = () => {
    setIsEditing(true);
    setEditingFilterId(null); // null means creating new
    setIsModalOpen(true);
    setSelectedAttributes([]);
    setAttributeFilters([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
    handleCancelEditAttribute();
  };

  const handleEditFilter = (filterId: string) => {
    setIsEditing(true);
    setEditingFilterId(filterId);
    setIsModalOpen(true);

    // Load filter into form
    const existingFilters = bulkPresetFiltersMap.get(filterId) || [];
    const loadedAttributes: Header[] = [];
    const loadedAttributeFilters: FilterProto[] = [];
    let savedDateFilter: DateFilter | undefined = undefined;

    existingFilters.forEach(filter => {
      if (filter.date_filter) {
        savedDateFilter = filter.date_filter;
      } else if (filter.kg_column_header?.id && filter.kg_column_header?.column_type === ColumnType.ATTRIBUTE) {
        loadedAttributes.push(filter.kg_column_header);
        loadedAttributeFilters.push(filter);
      }
    });

    setSelectedAttributes(loadedAttributes);
    setAttributeFilters(loadedAttributeFilters);
    setDateFilter(savedDateFilter);

    // Set date filter category based on loaded date filter
    if (savedDateFilter) {
      setDateFilterCategory(getDateFilterCategory(savedDateFilter));
    }

    handleCancelEditAttribute();
  };

  const handleCopyFilter = (filterId: string) => {
    setIsEditing(true);
    setEditingFilterId(null); // null means creating new copy
    setIsModalOpen(true);

    // Load filter into form
    const existingFilters = bulkPresetFiltersMap.get(filterId) || [];
    const loadedAttributes: Header[] = [];
    const loadedAttributeFilters: FilterProto[] = [];
    let savedDateFilter: DateFilter | undefined = undefined;

    existingFilters.forEach(filter => {
      if (filter.date_filter) {
        savedDateFilter = filter.date_filter;
      } else if (filter.kg_column_header?.id && filter.kg_column_header?.column_type === ColumnType.ATTRIBUTE) {
        loadedAttributes.push(filter.kg_column_header);
        loadedAttributeFilters.push(filter);
      }
    });

    setSelectedAttributes(loadedAttributes);
    setAttributeFilters(loadedAttributeFilters);
    setDateFilter(savedDateFilter);

    // Set date filter category based on loaded date filter
    if (savedDateFilter) {
      setDateFilterCategory(getDateFilterCategory(savedDateFilter));
    }

    handleCancelEditAttribute();
  };

  const handleDeleteFilter = (filterId: string) => {
    const newMap = new Map(bulkPresetFiltersMap);
    newMap.delete(filterId);
    setBulkPresetFiltersMap(newMap);
    setIsEditing(false);
    setEditingFilterId(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingFilterId(null);
    setIsModalOpen(false);
    setSelectedAttributes([]);
    setAttributeFilters([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
    handleCancelEditAttribute();
  };

  const handleSaveFilter = () => {
    const convertedFilters: FilterProto[] = [];

    // Add attribute filters (only those with operator and values)
    attributeFilters.forEach(filter => {
      if (filter.operator !== undefined && filter.values && filter.values.length > 0) {
        convertedFilters.push(filter);
      }
    });

    // Add temporal filter if present
    if (dateFilter && temporalColumns.length > 0) {
      const temporalColumn = temporalColumns[0];
      if (temporalColumn?.kg_column?.header) {
        convertedFilters.push({
          kg_column_header: temporalColumn?.kg_column?.header,
          date_filter: dateFilter,
          operator: FilterOperator.EQ,
          values: [],
          calculated_date_ranges: [],
        });
      }
    }

    // Validate that at least one filter has been configured
    if (convertedFilters.length === 0) {
      return; // Don't save empty filters
    }

    // Save to map
    const newMap = new Map(bulkPresetFiltersMap);

    if (editingFilterId) {
      // Updating existing filter
      newMap.set(editingFilterId, convertedFilters);
    } else {
      // Creating new filter - generate new ID
      const newId = String(bulkPresetFiltersMap.size + 1);
      newMap.set(newId, convertedFilters);
    }

    setBulkPresetFiltersMap(newMap);
    setIsEditing(false);
    setEditingFilterId(null);
    setIsModalOpen(false);
    setSelectedAttributes([]);
    setAttributeFilters([]);
    handleCancelEditAttribute();
  };

  // Check if an attribute is selected
  const isAttributeSelected = (header: Header) => {
    if (!header?.id) return false;
    return selectedAttributes.some((item) => item.id === header.id);
  };

  // Get filter display string
  const renderPresetFilters = (filters: FilterProto[]) => {
    if (!filters || filters.length === 0) {
      return null;
    }

    return (
      <HStack w="100%" textAlign="left" alignItems="stretch">
        <Wrap
          spacing={1}
          justify="flex-start"
          css={{
            // Remove default list spacing from Chakra Wrap (Wrap renders a <ul> tag)
            lineHeight: 0,
            "& > ul": {
              margin: 0,
              padding: 0,
            }
          }}
        >
          {filters.map((filter, filterIndex) => {
            let displayText = "";
            if (filter.date_filter) {
              displayText = `${filter.kg_column_header?.name}: ${getFilterDisplayString(filter)}`;
            } else if (filter.kg_column_header && filter.values && filter.values.length > 0) {
              const operatorSymbol = getOperatorSymbol(filter.operator!);
              displayText = `${filter?.kg_column_header?.name} ${operatorSymbol} ${filter.values.join(", ")}`;
            }

            return (
              <WrapItem key={filterIndex}>
                <Tag
                  size="md"
                  colorScheme={filter?.is_hidden ? "gray" : "purple"}
                  fontWeight="600"
                  flexShrink={0}
                  bg={filter?.is_hidden ? "gray.50" : "purple.50"}
                  borderWidth="1px"
                  borderColor={filter?.is_hidden ? "gray.300" : "purple.300"}
                  px={2}
                  py={1}
                >
                  {displayText}
                </Tag>
              </WrapItem>
            );
          })}
        </Wrap>
      </HStack>
    );
  };

  return (
    <VStack spacing={1} align="stretch" p={2} borderWidth="1px" borderColor="gray.200" borderRadius="md">
      <FormControl>
        <HStack justify="space-between" spacing={2}>
          <HStack
            spacing={2}
            cursor="pointer"
            p={1}
            borderRadius="md"
            m={1}
          >
            <Icon as={FiFilter} boxSize={5} color="fg.default" />
            <Text fontSize="md" fontWeight="700" color="gray.800" mb={0}>
              Constraints
            </Text>
            <Tooltip
              label={getTooltip("preset_filter.constraints.tooltip") || ""}
              placement="top"
              hasArrow
              bg="gray.700"
              color="white"
              fontSize="sm"
              px={3}
              py={2}
              borderRadius="md"
              ml={2}
            >
              <Box as="span" cursor="help">
                <Icon as={IoMdInformationCircleOutline} boxSize={6} color="blue.300" />
              </Box>
            </Tooltip>
            {savedFiltersCount > 0 && (
              <Badge colorScheme="purple" borderRadius="full">
                {savedFiltersCount}
              </Badge>
            )}
          </HStack>
          {/* Show add button if not editing */}
          {!isEditingScenario && (
            <HStack justify="flex-end">
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                colorScheme="purple"
                variant="outline"
                onClick={handleAddNewFilter}
                px={2}
                py={2}
              >
                Add New Filter
              </Button>
            </HStack>
          )}
        </HStack>
        <VStack spacing={4} align="stretch" p={1} overflowY="auto">
          {/* Show saved filters - hide when editing existing filter, show when copying or not editing */}
          {savedFiltersCount > 0 && (!isEditing || (isEditing && !editingFilterId)) && (
            <VStack spacing={1} align="stretch">
              {Array.from(bulkPresetFiltersMap.entries())
                .filter(([, filters]) => filters && filters.length > 0)
                .map(([filterId, filters]) => (
                <Box
                  key={filterId}
                  p={2}
                  position="relative"
                  opacity={isEditing && !editingFilterId ? 0.7 : 1}
                  pb={1}
                >
                  <HStack justify="space-between" m={0}>
                    {renderPresetFilters(filters)}
                    {!isEditing && (
                      <HStack spacing={2} m={0}>
                        {!isEditingScenario && (
                          <IconButton
                            aria-label="Copy filter"
                            icon={<FiCopy />}
                            size="xs"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleCopyFilter(filterId)}
                          />
                        )}
                        <IconButton
                          aria-label="Edit filter"
                          icon={<FiEdit2 />}
                          size="xs"
                          colorScheme="purple"
                          variant="ghost"
                          onClick={() => handleEditFilter(filterId)}
                        />
                        <IconButton
                          aria-label="Delete filter"
                          icon={<FiTrash2 />}
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDeleteFilter(filterId)}
                        />
                      </HStack>
                    )}
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}

          {/* Show add button if not editing */}
          {/* {!isEditingScenario && (
            <HStack justify="flex-start">
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                colorScheme="purple"
                variant="outline"
                onClick={handleAddNewFilter}
              >
                Add New Filter
              </Button>
            </HStack>
          )} */}

          {/* Modal for editing or creating filters */}
          <Modal
            isOpen={isModalOpen}
            onClose={handleCancelEdit}
            size="3xl"
            scrollBehavior="inside"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                {editingFilterId ? "Edit Filter" : "Create New Filter"}
              </ModalHeader>
              <ModalCloseButton />

              <ModalBody pb={6}>
                <VStack spacing={3} align="stretch">
                  {/* Temporal Filter Section - Show at top */}
                  {temporalColumns.length > 0 && (
                    <Box>
                      <HStack spacing={2} align="center" mb={2}>
                        <Text fontSize="sm" fontWeight="600" textAlign="left" m={0}>
                          {temporalColumns[0]?.kg_column?.header?.name || "Date"}
                        </Text>
                        {!dateFilter ? (
                          <Button
                            size="xs"
                            leftIcon={<FiPlus />}
                            colorScheme="blue"
                            variant="ghost"
                            border="1px solid"
                            borderColor="blue.300"
                            onClick={() => {
                              setDateFilter(DateFilter.create({}));
                              setDateFilterCategory(DateFilterCategory.FIXED);
                            }}
                          >
                            Add Date Filter
                          </Button>
                        ) : (
                          <IconButton
                            aria-label="Remove date filter"
                            icon={<FiX />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                              setDateFilter(undefined);
                              setDateFilterCategory(DateFilterCategory.FIXED);
                            }}
                          />
                        )}
                      </HStack>

                      {/* Show GlobalEntity when date filter is set */}
                      {dateFilter && (
                        <Box
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                          bg="white"
                          borderColor="blue.300"
                        >
                          <GlobalEntity
                            dateFilter={dateFilter}
                            setDateFilter={setDateFilter}
                            dateFilterCategory={dateFilterCategory}
                            setDateFilterCategory={setDateFilterCategory}
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Attribute Selection */}
                  {attributeColumns.length > 0 && (
                    <FormControl>
                      <HStack align="center" spacing={3}>
                        <Text fontSize="sm" mb={0} fontWeight="600" minW="120px" textAlign="left">
                          Attributes
                        </Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton
                            as={Button}
                            rightIcon={<FiChevronDown />}
                            size="sm"
                            variant="outline"
                            flex="1"
                            textAlign="left"
                            fontWeight="normal"
                          >
                            {selectedAttributes.length > 0
                              ? `${selectedAttributes.length} selected`
                              : "Select attributes"}
                          </MenuButton>
                          <MenuList maxH="200px" overflowY="auto">
                            {attributeColumns.map((columnScope) => {
                              const column = columnScope?.kg_column?.header;
                              if (!column?.id) return null;

                              return (
                                <MenuItem
                                  key={column.id}
                                  closeOnSelect={false}
                                  onClick={() => toggleAttributeSelection(column)}
                                  pl={2}
                                >
                                  <Checkbox
                                    isChecked={isAttributeSelected(column)}
                                    onChange={() => toggleAttributeSelection(column)}
                                    pointerEvents="none"
                                    colorScheme="purple"
                                    size="sm"
                                  >
                                    {column.name}
                                  </Checkbox>
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </Menu>
                      </HStack>

                      {/* Selected Attributes Tags */}
                      {selectedAttributes.length > 0 && (
                        <Wrap spacing={2} mt={2}>
                          {selectedAttributes.map((attr) => (
                            <WrapItem key={attr.id}>
                              <Tag size="sm" colorScheme="purple" borderRadius="full">
                                <TagLabel>{attr.name}</TagLabel>
                                <TagCloseButton onClick={() => toggleAttributeSelection(attr)} />
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      )}
                    </FormControl>
                  )}

                  {/* Values Section - shown only when editing an attribute */}
                  {editingAttributeFilter && showValuesSection && (
                    <Box
                      border="1px"
                      borderColor="purple.300"
                      borderRadius="md"
                      p={3}
                      bg="white"
                    >
                      <VStack spacing={3} align="stretch">
                        <HStack justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="600" color="purple.700" textAlign="left">
                            {`Edit Filter: ${editingAttributeFilter.kg_column_header?.name}`}
                          </Text>
                          <IconButton
                            aria-label="Cancel"
                            variant="ghost"
                            icon={<FiX />}
                            size="xs"
                            onClick={handleCancelEditAttribute}
                          />
                        </HStack>

                        {/* Operator and Values Selection Row */}
                        <HStack spacing={3} align="flex-start">
                          {/* Operator Selection */}
                          <FormControl maxW="120px">
                            <FormLabel fontSize="xs" fontWeight="600" mb={1}>
                              Operator
                            </FormLabel>
                            <Select
                              size="sm"
                              value={selectedFilterOperator}
                              onChange={(e) => setSelectedFilterOperator(Number(e.target.value) as FilterOperator)}
                              fontWeight="500"
                            >
                              <option value={FilterOperator.EQ}>Equals (EQ)</option>
                              <option value={FilterOperator.NE}>Not Equals (NE)</option>
                            </Select>
                          </FormControl>

                          {/* Values Multi-Select */}
                          <FormControl flex="1">
                            <FormLabel fontSize="xs" fontWeight="600" mb={1}>
                              Select Values
                            </FormLabel>
                            {isLoadingValues ? (
                              <HStack justify="center" py={2}>
                                <Spinner size="sm" />
                                <Text fontSize="sm" mb={0}>Loading values...</Text>
                              </HStack>
                            ) : (
                              <Menu closeOnSelect={false}>
                                <MenuButton
                                  as={Button}
                                  rightIcon={<FiChevronDown />}
                                  size="sm"
                                  variant="outline"
                                  width="100%"
                                  textAlign="left"
                                  fontWeight="normal"
                                >
                                  {selectedFilterValues.length > 0
                                    ? `${selectedFilterValues.length} selected`
                                    : "Select values"}
                                </MenuButton>
                                <MenuList maxH="300px" overflowY="auto">
                                  {valuesList.length > 0 ? (
                                    valuesList.map((value) => (
                                      <MenuItem
                                        key={value}
                                        closeOnSelect={false}
                                        onClick={() => {
                                          if (selectedFilterValues.includes(value)) {
                                            setSelectedFilterValues(prev => prev.filter(v => v !== value));
                                          } else {
                                            setSelectedFilterValues(prev => [...prev, value]);
                                          }
                                        }}
                                        pl={2}
                                      >
                                        <Checkbox
                                          isChecked={selectedFilterValues.includes(value)}
                                          colorScheme="purple"
                                          pointerEvents="none"
                                          size="sm"
                                        >
                                          {value}
                                        </Checkbox>
                                      </MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem isDisabled>No values available</MenuItem>
                                  )}
                                </MenuList>
                              </Menu>
                            )}
                          </FormControl>

                          {/* Save Filter Button */}
                          <Box pt={6}>
                            <IconButton
                              aria-label="Save filter"
                              icon={<FiPlus />}
                              size="sm"
                              colorScheme="purple"
                              onClick={handleSaveEditedAttributeFilter}
                              isDisabled={selectedFilterValues.length === 0}
                            />
                          </Box>
                        </HStack>

                        {/* Selected Values Display */}
                        {selectedFilterValues.length > 0 && (
                          <Wrap spacing={2}>
                            {selectedFilterValues.map((value) => (
                              <WrapItem key={value}>
                                <Tag size="sm" colorScheme="purple" borderRadius="full">
                                  <TagLabel>{value}</TagLabel>
                                  <TagCloseButton
                                    onClick={() => setSelectedFilterValues(prev => prev.filter(v => v !== value))}
                                  />
                                </Tag>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* Display Applied Attribute Filters */}
                  {attributeFilters.length > 0 && (
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      backgroundColor="purple.50"
                      borderColor="purple.300"
                    >
                      <Text fontSize="sm" fontWeight="600" color="fg.default" mb={2}>
                        Applied Preset-Filters:
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {attributeFilters.map((filter, index) => (
                          <HStack key={filter.filter_id} justify="space-between">
                            <HStack flex="1">
                              {filter.values && filter.values.length > 0 ? (
                                <>
                                  <Tag size="md" colorScheme="purple" fontWeight="500">
                                    {filter.kg_column_header?.name} {filter.operator === FilterOperator.EQ ? "=" : "!="}
                                  </Tag>
                                  <HStack spacing={1}>
                                    {filter.values.slice(0, 3).map((value, idx) => (
                                      <Tag key={idx} size="sm" variant="outline" colorScheme="purple">
                                        {value}
                                      </Tag>
                                    ))}
                                    {filter.values.length > 3 && (
                                      <Tag size="sm" variant="outline" colorScheme="purple">
                                        +{filter.values.length - 3} more
                                      </Tag>
                                    )}
                                  </HStack>
                                </>
                              ) : (
                                <Tag size="md" colorScheme="gray" fontWeight="500">
                                  {filter.kg_column_header?.name} (not configured)
                                </Tag>
                              )}
                            </HStack>
                            <HStack spacing={1}>
                              <IconButton
                                aria-label="Edit filter"
                                icon={<FiEdit2 />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => fetchValuesForEditingAttributeFilter(filter, index)}
                                isLoading={loadingFilterIndex === index}
                                spinner={<Spinner size="sm" color="purple.500" thickness="2px" />}
                                isDisabled={loadingFilterIndex !== -1 && loadingFilterIndex !== index} // Disable all buttons except the loading one
                              />
                              <IconButton
                                aria-label="Remove filter"
                                icon={<FiTrash2 />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleRemoveAttributeFilter(filter.filter_id || "")}
                                isDisabled={loadingFilterIndex !== -1} // Disable while loading
                              />
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <HStack spacing={2} justify="flex-end" mt={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      onClick={handleSaveFilter}
                    >
                      {editingFilterId ? "Update" : "Add"}
                    </Button>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Empty state */}
          {savedFiltersCount === 0 && !isEditing && (
            <Text fontSize="sm" color="gray.600" textAlign="center" py={4}>
              No preset filters added yet. Click "Add New Filter" to create one.
            </Text>
          )}
        </VStack>
      </FormControl>
    </VStack>
  );
};

export default PresetFilters;
